// src/components/ProductDetail.js
import React, { useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom'; // Keep useParams for product :id
import './main.css'; // Common styles
import Flag from "./flag.webp"; // Indian flag icon
import ImageViewer from 'react-simple-image-viewer'; // Modal image viewer
// Removed ProductList import
import Sample from "./sample.svg"; // Placeholder image
import Newtab from "./newtab.png"; // Icon for external links
import Enquire from './Enquire'; // Enquiry modal component
import ProductList from './ProductList';
// Receive products array, loggedInCustomerCode, and customerDiscount as props
const ProductDetail = ({ products = [], loggedInCustomerCode, customerDiscount }) => {

  // State for UI elements
  const [currentImage, setCurrentImage] = useState(0); // Index for image viewer
  const [isViewerOpen, setIsViewerOpen] = useState(false); // Image viewer modal state
  const [isEnquireOpen, setIsEnquireOpen] = useState(false); // Enquiry modal state

  // Get product identifier (:id) from the URL
  const { id: productIdFromUrl } = useParams(); // Rename id to avoid conflict

  // --- Data Fetching & Processing ---
  // Find the specific product from the products array passed via props
  const product = useMemo(() => {
    if (!productIdFromUrl || !products || products.length === 0) return null;
    // Find product where Firestore doc ID matches the URL parameter
    // (Using Firestore ID is more reliable than encoded product names)
    return products.find((p) => p.id === productIdFromUrl);
  }, [products, productIdFromUrl]);

  // Use the customerDiscount prop passed from App.js
  const discountPercent = customerDiscount;

  // Process images for the viewer and thumbnails
  const allImages = useMemo(() => {
    if (!product || !product.image) return [Sample]; // Return placeholder if no main image
    const mainImg = product.image;
    // Safely access subimages, assuming it's an object of URLs or null/undefined
    const subImgs = (product.subimages && typeof product.subimages === 'object')
      ? Object.values(product.subimages).filter(Boolean) // Get valid URLs from object values
      : [];
    return [mainImg, ...subImgs].filter(Boolean); // Combine and filter out any empty strings
  }, [product]);

  // Price calculations using the specific product and discount
  const { discountedSellingPrice, sampleSellingPrice, rate2Discounted, rate3Discounted } = useMemo(() => {
    if (!product || typeof product.Rate !== 'number') {
      return { discountedSellingPrice: 'N/A', sampleSellingPrice: 'N/A', rate2Discounted: 'N/A', rate3Discounted: 'N/A' };
    }
    const baseSellingPrice = product.Rate; // Base price (for 999+)
    const rate2 = typeof product.rate2 === 'number' ? product.rate2 : baseSellingPrice; // Fallback for rate2
    const rate3 = typeof product.rate3 === 'number' ? product.rate3 : rate2; // Fallback for rate3

    // Calculate discounted prices for all tiers
    const calculateDiscount = (price) => (price * (1 - discountPercent / 100)).toFixed(2);

    return {
      discountedSellingPrice: calculateDiscount(baseSellingPrice), // Discounted price for 999+
      rate2Discounted: calculateDiscount(rate2), // Discounted price for 499-999
      rate3Discounted: calculateDiscount(rate3), // Discounted price for 200-499
      sampleSellingPrice: (baseSellingPrice * 2.5).toFixed(2) // Sample price logic remains
    };
  }, [product, discountPercent]);

  // --- Event Handlers ---
  const openImageViewer = useCallback((index) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = useCallback(() => {
    setCurrentImage(0); // Reset index on close
    setIsViewerOpen(false);
  }, []);

  const handleEnquireClick = useCallback(() => {
    setIsEnquireOpen(true);
  }, []);

  const handleCloseEnquire = useCallback(() => {
    setIsEnquireOpen(false);
  }, []);

  // --- Render Logic ---

  // Show loading or not found message if product data isn't available yet
  if (!product) {
    // Added basic styling for visibility, adjust as needed
    return (
        <div style={{ paddingTop: '150px', paddingBottom: '50px', textAlign: 'center', fontSize: '1.2em' }}>
            Loading product details or product not found...
        </div>
     );
  }

  // Extract 'aka' tags safely
  const akaTags = product.aka ? product.aka.split(',').map(tag => tag.trim()).filter(Boolean) : [];
console.log(customerDiscount)
  return (
    <>
      {/* Header and Tophead are now handled globally in App.js */}

      {/* Main content wrapper for the product detail page */}
      {/* Add top padding matching the global header height if it's fixed/sticky */}
      <div className="aboutproduct product-detail-page-content">

        {/* Breadcrumb Navigation */}
        <div className="categorytext">
          Category :
          <span> <Link to="/products"> Memory foam</Link> > Sleeping accessories > </span> {/* Link to all products */}
          {product["Product name"]} {/* Current product name */}
        </div>

        {/* Product Title */}
        <h2 className="headingg">Amcouch Memory foam {product["Product name"]}</h2>

        {/* Seller Info */}
        <div className="seller">
          <span>Amcouch by</span>
          <a href="https://www.amcoindus.net" target="_blank" rel="noopener noreferrer"> <span>Amco industries.</span></a>
          <span>7yr+</span> <img height={13} src={Flag} alt="Indian Flag" /> <span>IND</span>
        </div>

        {/* Main Product Detail Layout (Image + Content) */}
        <div className="product-detail">
          {/* Image Section */}
          <div className="imagesection">
            {/* Main Image */}
            <img
              src={allImages[0]} // Display the first image (main or placeholder)
              alt={product["Product name"] || 'Product Image'}
              className="main-image"
              onClick={() => openImageViewer(0)} // Open viewer at index 0
              onError={(e) => { e.target.onerror = null; e.target.src = Sample; }} // Fallback image
              style={{ cursor: allImages.length > 1 ? 'pointer' : 'default' }} // Indicate clickability
            />
            {/* Thumbnails (Sub-images) */}
            <div className="subimages">
              {allImages.length > 1 && allImages.slice(1).map((src, index) => (
                <img
                  src={src}
                  onClick={() => openImageViewer(index + 1)} // Open viewer at correct index
                  key={index + 1}
                  alt={`Product view ${index + 2}`}
                  onError={(e) => { e.target.onerror = null; e.target.src = Sample; }} // Fallback
                  style={{ cursor: 'pointer' }}
                />
              ))}
            </div>
          </div>

          {/* Content Section */}
          <div className="content">
            {/* Tags */}
            <div className="tags">
              <div className="customisable"><p>Customisable</p></div>
              <div className="coloroptions"><p>15+ colors</p></div>
              {product.isTrending && <div className="trending-tag"><p>Trending 🔥</p></div>} {/* Show if trending */}
            </div>
            {/* SKU */}
            <p className="sku">Amcouch Product SKU: {product.SKU || 'N/A'}</p>
            <br />
            {/* Price Header */}
            <div className="lowest">Raw Foam (Excl. GST)</div>

            {/* Tiered Pricing Display */}
            <div className="priceingsection">
              {/* Tier 3 (200-499) */}
              <div className="first"><div className="qty">200-499 pcs</div> <div className="pricetag">₹{rate3Discounted}</div></div>
              {/* Tier 2 (499-999) */}
              <div className="second"><div className="qty">499-999 pcs</div> <div className="pricetag">₹{rate2Discounted}</div></div>
              {/* Tier 1 (999+) */}
              <div className="third"><div className="qty">999+ pcs</div> <div className="pricetag">₹{discountedSellingPrice}</div></div>
            </div>

            {/* Sample Product Section */}
            <div className="productsample">
              <div className='smplett'>
                <img src={Sample} height={25} alt="Sample Icon" />
                <div className="smpletext"><h3>Sample price:</h3> <span> ₹{sampleSellingPrice}</span></div>
              </div>
              <div className="samplebtn">
                {/* Use anchor tag styled as button to trigger modal */}
                <a onClick={handleEnquireClick} role="button">Get Sample</a>
              </div>
            </div>

            {/* Product Description */}
            <p className="description">
              Description: <span>
                {product.description || `High-quality memory foam ${product["Product name"]}. Ideal for wholesale comfort solutions.`}
              </span>
            </p>

            {/* Product Variations */}
            <div className="dimentions">
              <h3>Variations Available</h3>
              <div className="dimensions-grid">
                <div className="dimension-item">
                  <span className="dim-label">Fabric Options</span>
                  <span className="dim-value">3+ Types</span>
                </div>
                <div className="dimension-item">
                  <span className="dim-label">Color Options</span>
                  <span className="dim-value">15+ Colors</span> {/* Updated count */}
                </div>
                <div className="dimension-item">
                  <span className="dim-label">Color Chart</span>
                  <span className="dim-value">
                    {/* Link to a color chart page/modal if available */}
                    <a href="/color-chart" target="_blank" rel="noopener noreferrer">
                      View Chart <img height={12} src={Newtab} alt="Opens in new tab" />
                    </a>
                  </span>
                </div>
              </div>
            </div>

            {/* Product Dimensions */}
            <div className="dimentions">
              <h3>Dimensions (Foam Core)</h3>
              <div className="dimensions-grid">
                <div className="dimension-item"> <span className="dim-label">Length (L)</span> <span className="dim-value">{product.L ?? 'N/A'} In</span> </div>
                <div className="dimension-item"> <span className="dim-label">Breadth (B)</span> <span className="dim-value">{product.B ?? 'N/A'} In</span> </div>
                <div className="dimension-item"> <span className="dim-label">Height (H)</span> <span className="dim-value">{product.H ?? 'N/A'} In</span> </div>
                <div className="dimension-item"> <span className="dim-label">Weight (Approx)</span> <span className="dim-value">{product.W ?? 'N/A'} kg</span> </div>
                {/* Example calculation - adjust based on actual packing */}
                <div className="dimension-item"> <span className="dim-label">Est. Pcs/Box</span> <span className="dim-value">~20</span> </div>
              </div>
            </div>
            <br />

            {/* Action Buttons */}
             {/* Call Now Button - Consider linking to a phone number */}
             {/* <div className="btns"> <a href="tel:+919811192812" className="btn">Call Now</a> </div> */}
             {/* Enquire Now Button */}
            <div className="btns">
              <button onClick={handleEnquireClick} className="btn">Final Price</button>
            </div>
          </div>
        </div>
      </div>
      <br /><br />

      {/* Also Known As / Tags Section */}
      {akaTags.length > 0 && (
        <>
          <h1 className="subheading">Also commonly known as</h1>
          <div className="aka-tags-container offering"> {/* Use appropriate class */}
            {akaTags.map((tag, index) => (
              <span key={index} className="tag aka-tag">{tag}</span>
            ))}
          </div>
          <br /><br />
        </>
      )}

      {/* Related Products Section - Consider if needed on detail page */}
      {/* Example: Could show products with similar tags or category */}
      
      <div className="related-products-section">
        <section className="products" id="related-products">
          <h1 className="heading"> You Might Also Like </h1>
          <ProductList initialLimit={4} customerDiscount={customerDiscount} filterCategory={product.category} excludeId={product.id} />
        </section>
      </div>
     


      {/* Image Viewer Modal */}
      {isViewerOpen && (
        <ImageViewer
          src={allImages} // Pass the array of image URLs
          currentIndex={currentImage} // Current image index to show
          disableScroll={false} // Allow background scroll when open?
          closeOnClickOutside={true} // Close when clicking the dark background
          onClose={closeImageViewer} // Function to call when closing
          backgroundStyle={{ backgroundColor: "rgba(0,0,0,0.9)" }} // Dark overlay
        />
      )}

      {/* Enquire Modal */}
      {/* Pass necessary props: modal state, close handler, product data, and customer code */}
      <Enquire
        isOpen={isEnquireOpen}
        onClose={handleCloseEnquire}
        customerDiscount={customerDiscount}
        product={product} // Pass the currently viewed product object
        customerCode={loggedInCustomerCode} // Pass the logged-in customer code
      />
    </>
  );
};

export default ProductDetail;